<template>
  <form ref="referenceRef" role="search" class="relative" :class="wrapperClass" @submit.prevent="submit">
    <UiInput
      :id="id"
      ref="inputRef"
      v-model="inputModel"
      type="search"
      autocomplete="off"
      :placeholder="placeholder"
      class="[appearance:textfield] [&::-webkit-search-cancel-button]:hidden"
      :wrapper-class="inputWrapperClass"
      :size="size"
      @keydown="handleInputKeyDown"
      @keydown.enter="submit"
      @focus="snippets?.length && open()"
    >
      <template #suffix>
        <UiIconButton
          v-if="inputModel"
          :id="`${id}-input-clear`"
          :icon="IconCloseCircle"
          variant="secondary"
          class="!bg-transparent hover:!bg-transparent focus:!bg-transparent"
          @click="reset"
        />
        <UiIconButton
          :id="`${id}-input-searchAction`"
          :icon="IconSearch"
          variant="secondary"
          class="!bg-transparent hover:!bg-transparent focus:!bg-transparent"
          @click="submit"
        />
      </template>
    </UiInput>
    <div v-if="isOpen" ref="floatingRef" :style="style" class="inset-x-0">
      <div
        class="flex min-h-20 w-full items-center justify-center rounded-md border border-solid border-neutral-300 bg-white p-2 drop-shadow-md"
      >
        <SfLoaderCircular v-if="isLoadingSnippets" />
        <ul v-else-if="snippets && snippets.length > 0" ref="dropdownListRef" class="w-full">
          <li v-for="snippet in snippets" :key="snippet.id">
            <UiListItem
              :id="`${id}-snippet-${snippet.id}`"
              tag="button"
              type="button"
              @click="navigateToSnippet(snippet)"
              @keydown.enter.space.prevent="navigateToSnippet(snippet)"
            >
              <div class="flex flex-row items-center justify-start space-x-4">
                <UiImage
                  :id="`${id}-snippet-${snippet.id}`"
                  :alt="snippet.name"
                  :src="snippet.image"
                  class="size-14 rounded-md object-cover"
                />
                <div class="flex flex-col items-start">
                  <UiText class="text-left">{{ snippet.name }}</UiText>
                  <UiText class="font-medium">
                    {{ snippet.text }}
                  </UiText>
                </div>
              </div>
            </UiListItem>
          </li>
        </ul>
        <UiText v-else>{{ $t('search.noResultFound') }}</UiText>
      </div>
    </div>
  </form>
</template>

<script setup lang="ts">
import { IconCloseCircle, IconSearch } from '#components';
import { offset } from '@floating-ui/vue';
import { unrefElement, watchDebounced } from '@vueuse/core';

import type { InputSearchProps, Snippet } from '~/components/ui/Input/types';

const goTo = useGoTo();

const props = defineProps<InputSearchProps>();

const inputModel = ref<string>(props.value ?? '');
const inputRef = ref<HTMLInputElement | null>(null);
const dropdownListRef = ref<HTMLUListElement | null>(null);
const isLoadingSnippets = ref<boolean>(false);
const snippets = ref<Snippet[] | null>(null);
const preventSearch = ref<boolean>(false);

const { close, isOpen, open } = useDisclosure();
const { floatingRef, referenceRef, style } = useDropdown({
  isOpen,
  middleware: [offset(4)],
  onClose: close,
  placement: 'bottom-start',
});
const { focusables: focusableElements } = useTrapFocus(dropdownListRef as Ref<HTMLElement>, {
  activeState: isOpen,
  arrowKeysUpDown: true,
  initialFocus: false,
  trapTabs: false,
});

const submit = (): void => {
  preventSearch.value = true;
  close();
  goTo(`/search?search=${inputModel.value}`);
};

const navigateToSnippet = (snippet: Snippet): void => {
  preventSearch.value = true;
  close();
  inputModel.value = snippet.name;
  goTo(`/${snippet.href}`);
};

const focusInput = (): void => {
  const inputEl = unrefElement(inputRef)?.querySelector('input');
  inputEl?.focus();
};

const reset = (): void => {
  inputModel.value = '';
  snippets.value = [];
  close();
  focusInput();
};

const handleInputKeyDown = (event: KeyboardEvent): void => {
  if (event.key === 'Escape') reset();
  if (event.key === 'ArrowUp') {
    open();
    if (isOpen && focusableElements.value.length > 0) {
      focusableElements.value[focusableElements.value.length - 1]?.focus();
    }
  }
  if (event.key === 'ArrowDown') {
    open();
    if (isOpen && focusableElements.value.length > 0) {
      focusableElements.value[0]?.focus();
    }
  }
};

watch(inputModel, () => {
  if (inputModel.value === '') reset();
});

watchDebounced(
  inputModel,
  () => {
    if (inputModel.value && !preventSearch.value) {
      const getSnippets = async (): Promise<void> => {
        open();
        isLoadingSnippets.value = true;
        try {
          snippets.value = await props.action(inputModel.value);
        } catch (error) {
          close();
          console.error(error);
        }
        isLoadingSnippets.value = false;
      };

      getSnippets();
    }

    preventSearch.value = false;
  },
  { debounce: 500 },
);
</script>
